import React, { useEffect, useState } from 'react';
import useAxios from 'axios-hooks';
import { PrimaryButton } from '@fluentui/react';
import { Field, Form, Formik } from 'formik';
import { useParams, useHistory } from 'react-router-dom';
import TextField from '../../../../Forms/TextField';
import Toast from '../../../../Forms/Toast';
import FeatureSummary from '../../../../Util/FeatureSummary';
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import DropDown from '../../../../Forms/DropDown';

const newCoBrandSettingsObject = {
    clientID: null,
    clientName: '',
    callbackUrl: '',
    webhookEventId: [],
};

const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        padding: "0 8px",
                        boxSizing: "border-box",
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

const WizardPage = () => {
    const [showToast, setShowToast] = useState(false);
    const [showError, setShowError] = useState(false);
    const history = useHistory();
    const { clientId: idFromParams } = useParams();

    const [{ data: domain }, getWebhook] = useAxios(
        { url: `/internal/cobrand/webhook/${idFromParams}`, method: 'GET' },
        { manual: true }
    );

    const [{ data: events }] = useAxios('/internal/cobrand/webhook-events');
    const [{ data: clients }] = useAxios('/internal/clients');

    const clientsOptions = clients?.map(u => ({ value: u.clientID, label: u.name })) || [];
    useEffect(() => {
        if (idFromParams) {
            getWebhook();
        }
    }, [idFromParams]);

    const [{ loading: submitting }, submitForm] = useAxios(
        { url: `/internal/cobrand/webhook/update` },
        { manual: true }
    );

    return (
        <Formik
            initialValues={domain || newCoBrandSettingsObject}
            onSubmit={async (values, { resetForm }) => {
                setShowError(false);
                const result = await submitForm({
                    url: "/internal/cobrand/webhook/create",
                    method: "POST",
                    data: values,
                });

                if (!result.isAxiosError) {
                    if (!idFromParams) {
                        history.push(`/client-settings-feature/webhooks/${result.data}/wizard`);
                    }
                    setShowToast(true);
                } else {
                    setShowError(true);
                }
            }}
            enableReinitialize
            isInitialValid={false}
        >
            {({ errors, values, setFieldValue, handleSubmit, dirty }) => {
                const isButtonDisabled = !dirty || !values.clientID || !values.callbackUrl || !values.webhookEventId?.length;

                const selectedWebhookEvents = events?.filter(event =>
                    values.webhookEventId?.includes(event.webhookEventId)
                );

                return (
                    <Form style={{ maxWidth: "500px", marginBottom: 500 }}>
                        <div className="displayflex mb20" style={{ justifyContent: 'space-between' }}>
                            <h4>Webhooks</h4>
                            <PrimaryButton
                                onClick={handleSubmit}
                                text={idFromParams ? "Update" : 'Create'}
                                disabled={isButtonDisabled}
                            />
                        </div>

                        {showError && (
                            <div style={{ color: 'rgb(164, 38, 44)' }}>
                                An error has occurred.
                            </div>
                        )}
                        <div className='panel-h' style={{fontWeight:'500', marginBottom:5}}>Client</div>
                        <Select
                            components={{ MenuList: CustomMenuList }}
                            options={clientsOptions}
                            placeholder="Select Client"
                            name="clientName"
                            value={clientsOptions.find(option => option.value === values.clientName)}
                            onChange={(option) => {
                                setFieldValue('clientID', option.value);
                                setFieldValue('clientName', option.value);
                            }}
                            isSearchable
                            styles={{
                                container: base => ({ ...base, width: 500 }),
                                control: base => ({ ...base, border: "1px solid #aaaaaa" }),
                                menuList: base => ({ ...base, maxHeight: "300px", overflowY: "auto" }),
                            }}
                        />

                        <TextField
                            label="Callback Url"
                            className="mt10"
                            name="callbackUrl"
                            value={values.callbackUrl}
                            onChange={(e, v) => setFieldValue('callbackUrl', v)}
                            errorMessage={errors.callbackUrl}
                        />

                        <DropDown
                            multiSelect
                            label="Webhook Events"
                            name="webhookEventId"
                            className="mt10"
                            selectedKeys={values.webhookEventId}
                            onChange={(e, item) => {
                                const selected = item.selected
                                    ? [...(values.webhookEventId || []), item.key]
                                    : (values.webhookEventId || []).filter(id => id !== item.key);
                                setFieldValue('webhookEventId', selected);
                            }}
                            options={events?.map(e => ({
                                key: e.webhookEventId,
                                text: e.eventName,
                            }))}
                            calloutProps={{
                                styles: { calloutMain: { maxHeight: 300, overflowY: 'auto' } },
                            }}
                        />

                        {selectedWebhookEvents?.length > 0 && (
                            <div className="mt20">
                                <h4>Selected Events</h4>
                                <div style={{ border: '1px solid #ccc', borderRadius: 4, marginTop: 8 }}>
                                    {selectedWebhookEvents.map(event => (
                                        <div
                                            key={event.webhookEventId}
                                            style={{
                                                padding: '10px',
                                                borderBottom: '1px solid #ccc',
                                                fontSize: 14
                                            }}
                                        >
                                            {event.eventName}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <Toast show={showToast} message="Saved!" onClose={() => setShowToast(false)} />
                        <FeatureSummary
                            label="About Webhooks"
                            summary="Configure webhook endpoints for client integrations. Set the callback URL where webhook payloads will be sent, and select which events should trigger notifications. Webhooks enable real-time integrations with external systems when user activities occur."
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

export default WizardPage;
